import { FC, ReactNode } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export type ProductApp = {
    readonly id: string;
    readonly name: string;
    readonly organization?: string;
    readonly icon: ReactNode;
    readonly settingUpCurrently?: boolean;
};

export type HoverProductCardProps = {
    readonly title: string;
    readonly organization?: string;
    readonly apps: ProductApp[];
    readonly className?: string;
    readonly dataEventProps?: DataEventProps;
    readonly position?: "top" | "bottom" | "left" | "right";
    readonly maxWidth?: string;
};

const cardContainerStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "padding": `${token("space.200")} ${token("space.300")}`,
    "backgroundColor": wacToken("color.neutral.light0"),
    "borderRadius": `${token("space.100")}`,
    "boxShadow": `0px 4px 12px ${wacToken(
        "color.neutral.light100"
    )}, 0px 0px 1px ${wacToken("color.neutral.light100")}`,
    "border": `1px solid ${wacToken("color.neutral.light200")}`,
    "minWidth": "280px",
    "maxWidth": "320px",
    "zIndex": 1000,
    "position": "relative",
    "transition": "all 0.2s ease-in-out",
    "&:hover": {
        boxShadow: wacToken("color.neutral.light100")
    }
});

const titleStyles = css({
    margin: 0,
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: "1.5rem",
    color: token("color.text.subtle"),
    fontFamily: "Atlassian Sans",
    borderBlockEnd: `2px solid ${token("color.border")}`,
    padding: `${token("space.200")} ${token("space.100")} ${token(
        "space.200"
    )} ${token("space.100")}`
});

const appsListStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: 0,
    listStyle: "none",
    margin: 0,
    padding: 0
});

const appItemStyles = css({
    "display": "flex",
    "alignItems": "center",
    "gap": `${token("space.100")}`,
    "padding": "8px",
    "borderRadius": `${token("space.050")}`,
    "transition": "background-color 0.2s ease-in-out",
    "textDecoration": "none",
    "color": "inherit",
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light100")
    }
});

const appIconStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    flexShrink: 0
});

const appContentStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    flex: 1
});

const appNameStyles = css({
    margin: 0,
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: "1.25rem",
    color: token("color.text.subtle"),
    fontFamily: "Atlassian Sans",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
});

const appDescriptionStyles = css({
    margin: 0,
    fontSize: "0.75rem",
    lineHeight: "1rem",
    color: wacToken("color.neutral.light700"),
    fontFamily: "Atlassian Sans",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
});

export const HoverProductCard: FC<HoverProductCardProps> = ({
    title,
    apps,
    className,
    dataEventProps,
    maxWidth = "320px"
}) => {
    const cardStyles = css({
        maxWidth
    });

    return (
        <div
            className={className}
            css={[cardContainerStyles, cardStyles]}
            {...dataEventProps}
        >
            <h3 css={titleStyles}>{title}</h3>

            {apps.length > 0 && (
                <ul css={appsListStyles}>
                    {apps.map((app) => (
                        <li key={app.id}>
                            <div css={[appItemStyles]}>
                                <div css={appIconStyles}>{app.icon}</div>
                                <div css={appContentStyles}>
                                    <div css={appNameStyles}>{app.name}</div>
                                    {app.organization && (
                                        <div css={appDescriptionStyles}>
                                            {app.organization}
                                        </div>
                                    )}
                                    {app.settingUpCurrently && (
                                        <div css={appDescriptionStyles}>
                                            Setting up currently
                                        </div>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
